import React, { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { PrimaryBtn } from 'components/UI/Buttons';
import CloseableAlert from 'components/UI/CloseableAlert';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import { useCreateOnboardingMutation } from 'services/api/onboarding.api';
import getReCaptchaToken from 'utils/getReCaptchaToken';
import { identifyPosthogUser } from 'utils/posthogUtils';
import validateEmail from 'utils/validations/validateEmail';

import { signInUser } from '../../redux/slices/user.slice';

const Onboarding: FC = () => {
  const [createOnboarding, { data, isLoading, isSuccess, error }] = useCreateOnboardingMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [emailInput, setEmailInput] = React.useState('');
  const [invalidEmail, setInvalidEmail] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    if (!validateEmail(emailInput)) {
      setInvalidEmail(true);
      return;
    }

    try {
      const recaptchaToken = await getReCaptchaToken();

      const onboardingData = {
        recaptchaToken,
        companyName: formData.get('companyName')?.toString() || '',
        userName: formData.get('userName')?.toString() || '',
        email: emailInput,
        password: formData.get('password')?.toString() || '',
        phoneNumber: formData.get('phoneNumber')?.toString() || '',
      };

      await createOnboarding(onboardingData);
    } catch (onboardingError) {
      // eslint-disable-next-line no-console
      console.error('Error during onboarding process:', onboardingError);
    }
  };

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(signInUser(data.user));
      identifyPosthogUser(data.user);
      navigate('/setup-onboarding');
    }
  }, [data, isSuccess, dispatch, navigate]);

  const errorMessage = error && 'data' in error ? String(error.data) : null;

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Grid sx={{ minHeight: '110vh' }} container component="main">
        <Grid item xs={12} component={Paper} elevation={6} square>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: { xs: '100%', lg: '60%' },
              mx: 'auto',
              height: '100vh',
              alignItems: 'center',
              pt: '60px',
            }}
          >
            {errorMessage && <CloseableAlert msg={errorMessage} severity="error" />}
            <img alt="Zeller" src="/logo.png" height="170" />
            <Typography variant="h4">Registro de Empresa</Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2, textAlign: 'center', color: 'text.secondary' }}>
              Completa los datos para crear tu cuenta y comenzar a usar Zeller
            </Typography>
            <Box
              sx={{
                bgcolor: 'backgroundGrey.main',
                p: 3,
                borderRadius: '20px',
                mt: 2,
                border: '1px solid hsla(210,18%,87%,1)',
                width: { xs: '88%', lg: '450px' },
              }}
              component="form"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                handleSubmit(e).catch((err) => {
                  // eslint-disable-next-line no-console
                  console.error(err);
                });
              }}
            >
              <TextField
                id="companyName"
                margin="normal"
                required
                fullWidth
                label="Nombre de la Empresa"
                name="companyName"
                autoComplete="organization"
                autoFocus
                sx={{ bgcolor: '#fff' }}
              />

              <TextField
                id="userName"
                margin="normal"
                required
                fullWidth
                label="Tu Nombre"
                name="userName"
                autoComplete="name"
                sx={{ bgcolor: '#fff' }}
              />

              <TextField
                id="email"
                margin="normal"
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                sx={{ bgcolor: '#fff' }}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setInvalidEmail(false);
                }}
                helperText={invalidEmail ? 'Por favor, ingresa un email válido' : ''}
                error={invalidEmail}
              />

              <TextField
                id="password"
                margin="normal"
                required
                fullWidth
                label="Contraseña"
                name="password"
                type="password"
                autoComplete="new-password"
                sx={{ bgcolor: '#fff' }}
                helperText="Mínimo 8 caracteres"
              />

              <TextField
                id="phoneNumber"
                margin="normal"
                required
                fullWidth
                label="Teléfono de Contacto"
                name="phoneNumber"
                type="tel"
                autoComplete="tel"
                sx={{ bgcolor: '#fff' }}
                helperText="Número de teléfono de la empresa"
              />

              <PrimaryBtn sx={{ mt: 3, py: 1, mb: 1 }} type="submit" fullWidth variant="contained">
                Crear Cuenta
              </PrimaryBtn>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ¿Ya tienes una cuenta?{' '}
                  <a href="/sign-in" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    Iniciar Sesión
                  </a>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default Onboarding;
